/* eslint-env browser */
// Public landing page (owner, 5 Nov 2026: "apply amazing design in the landing
// page ... use the new ios and android screenshot"): the real app on the page.
// The hero shows the Android home and the iPhone finding a driver; then the four
// promises, the three steps of a booking, three features up close, the drivers'
// side, the tutorial videos and the download band with the stores' badges.
// Screens: web-sized WebP of the store captures (src/assets/img/landing), drawn in
// CSS phones (components/landing/PhoneFrame), with the live app's words ("Where
// to?"). Words: lp_* keys with English defaults; am / om / ti in
// json/languages-landing.add.json (for /languages) and, the same words, bundled in
// components/landing/landingWords.js, so a language switch works before the push.
// Copy rule: customer and trip, never rider or ride. Motion: entrances only, each
// once and within 5 s (WCAG 2.2.2); none with Reduce Motion.
import React from 'react';
import Header from 'components/Header/Header.js';
import HomeFooter from 'components/Footer/HomeFooter.js';
import HeaderLinks from 'components/Header/HeaderLinks.js';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, Button, Grid, Stack, GlobalStyles } from '@mui/material';
import { keyframes } from '@mui/system';
import {
  ArrowForward,
  CheckCircle,
  ReceiptLong,
  NearMe,
  AccountBalanceWallet,
  SupportAgent,
  LocalTaxi,
  AltRoute,
  Celebration,
  TravelExplore,
} from '@mui/icons-material';
import VideoCarousel from '../components/VideoCarousel';
import PhoneFrame from '../components/landing/PhoneFrame';
import Reveal from '../components/landing/Reveal';
import NearViewport from '../components/landing/NearViewport';
import CityStreets from '../components/landing/CityStreets';
import StoreButtons, { srOnly } from '../components/landing/StoreButtons';
import landingWord from '../components/landing/landingWords';
import { tokens as c, iconHues, FONT_STACK } from '../theme';
import iosFinding from '../assets/img/landing/ios-finding.webp';
import iosHome from '../assets/img/landing/ios-home.webp';
import iosConfirm from '../assets/img/landing/ios-confirm.webp';
import iosAccepted from '../assets/img/landing/ios-accepted.webp';
import iosEvents from '../assets/img/landing/ios-events.webp';
import iosRequests from '../assets/img/landing/ios-requests.webp';
import androidHome from '../assets/img/landing/android-home.webp';
import androidFinding from '../assets/img/landing/android-finding.webp';
import androidStops from '../assets/img/landing/android-stops.webp';
import androidDrive from '../assets/img/landing/android-drive.webp';
import androidAccepted from '../assets/img/landing/android-accepted.webp';
import videoPoster from '../assets/img/landing/video-poster.jpg';

const dashboardRoutes = [];

// This page's own shades: the warm night of the hero and the drivers' band, the
// light orange that reads on it (9:1), the headline's glow and the live dot.
const NIGHT = '#130C09';
const NIGHT_2 = '#21150F';
const NIGHT_3 = '#0C0806';
const ORANGE_ON_NIGHT = '#FF9A73';
const ORANGE_LIGHT = '#FFB08F';
const ORANGE_GLOW = '#FF7A45';
const LIVE_GREEN = '#22C55E';
const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';

const rise = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: none; }
`;
const draw = keyframes`
  from { stroke-dashoffset: 1400; }
  to { stroke-dashoffset: 0; }
`;
const ping = keyframes`
  0% { transform: scale(0.6); opacity: 0.85; }
  100% { transform: scale(2.8); opacity: 0; }
`;
const still = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };
// Arriving once on load (the hero needs no scroll to be seen).
const enter = (delay) => ({ animation: `${rise} 800ms ${EASE} ${delay}ms both`, ...still });

// The intro opens on real footage at 9 s (at 6 s it was a white logo card).
const videoSources = [
  { src: 'https://storage.googleapis.com/donkeydrive-videos/Intro.mp4', thumbnailTime: 9 },
  { src: 'https://storage.googleapis.com/donkeydrive-videos/video2.mp4', thumbnailTime: 0 },
  { src: 'https://storage.googleapis.com/donkeydrive-videos/video5.mp4', thumbnailTime: 0 },
  { src: 'https://storage.googleapis.com/donkeydrive-videos/video7.mp4', thumbnailTime: 0 },
  { src: 'https://storage.googleapis.com/donkeydrive-videos/video8.mp4', thumbnailTime: 3 },
];

// Streets of a city at night behind the hero (CityStreets, faded towards the edges), and on
// wide screens one trip drawing itself to a pin right of the phones, as the app
// draws the route on the confirmation. Both drawings keep the right edge in view
// (xMaxYMid), so the pin and its ring stay together at every width.
function HeroMap() {
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <CityStreets />
      <Box
        component="svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMaxYMid slice"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: { xs: 'none', lg: 'block' } }}
      >
        <Box
          component="path"
          d="M640 940 C 700 830, 820 790, 960 800 S 1250 720, 1310 560 S 1370 360, 1395 300"
          fill="none"
          stroke={c.ACCENT}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray="1400"
          opacity="0.85"
          sx={{ animation: `${draw} 2.6s cubic-bezier(0.77, 0, 0.175, 1) 0.3s both`, ...still }}
        />
        <circle cx="1395" cy="300" r="10" fill={c.ACCENT} stroke={c.WHITE} strokeWidth="3" />
        <Box
          component="circle"
          cx="1395"
          cy="300"
          r="11"
          fill="none"
          stroke={c.ACCENT}
          strokeWidth="2"
          sx={{ transformBox: 'fill-box', transformOrigin: 'center', opacity: 0, animation: `${ping} 2s ease-out 2.8s 1`, ...still }}
        />
      </Box>
    </Box>
  );
}

// A small card by the hero phones. Decoration (the phones' alt texts describe
// the screens), so screen readers skip it.
function FloatCard({ icon, iconFg, iconBg, title, sub, sx, delay = 0 }) {
  return (
    <Box aria-hidden sx={{ position: 'absolute', zIndex: 3, ...enter(delay), ...sx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.25,
          pl: 1.25,
          pr: 2.25,
          borderRadius: '18px',
          bgcolor: 'rgba(255, 255, 255, 0.97)',
          color: c.INK,
          boxShadow: '0 28px 50px -20px rgba(0, 0, 0, 0.6)',
        }}
      >
        <Box sx={{ width: 42, height: 42, borderRadius: '50%', bgcolor: iconBg, color: iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.3, color: c.INK }}>{title}</Typography>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.35, color: c.INK_2 }}>{sub}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function SectionHead({ eyebrow, title, sub, center = true, dark = false, track, id }) {
  return (
    <Box sx={{ textAlign: center ? 'center' : 'left', maxWidth: center ? 760 : 560, mx: center ? 'auto' : 0 }}>
      {eyebrow ? (
        <Typography sx={{ color: dark ? ORANGE_ON_NIGHT : c.ACCENT_TEXT, fontWeight: 700, fontSize: 14, letterSpacing: track('0.14em'), textTransform: 'uppercase', mb: 1.5 }}>
          {eyebrow}
        </Typography>
      ) : null}
      <Typography component="h2" id={id} sx={{ fontWeight: 700, fontSize: { xs: 34, md: 46 }, lineHeight: 1.1, letterSpacing: track('-0.025em'), color: dark ? c.WHITE : c.INK, textWrap: 'balance' }}>
        {title}
      </Typography>
      {sub ? (
        <Typography sx={{ mt: 2, fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: dark ? 'rgba(255, 255, 255, 0.76)' : c.INK_2, textWrap: 'pretty' }}>
          {sub}
        </Typography>
      ) : null}
    </Box>
  );
}

function CheckList({ items, dark = false }) {
  return (
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.5 }}>
      {items.map((text, i) => (
        <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <CheckCircle sx={{ fontSize: 22, mt: '2px', color: dark ? ORANGE_ON_NIGHT : c.SUCCESS, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 17, lineHeight: 1.5, color: dark ? 'rgba(255, 255, 255, 0.88)' : c.INK, textWrap: 'pretty' }}>{text}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// Only the top of the phone, fading out: the part of the screen the feature is
// about, larger (the stops screen's lower half is the keyboard). The box reaches 64 px
// past the phone's column on each side (and pads it back): the frame's drop shadow
// spreads about 60 px, and a box only 16 px wider cut it into a dark band down each
// side (owner, 5 Nov 2026: "dark left and right border" on these two pictures).
function CroppedPhone({ height, children }) {
  return (
    <Box
      sx={{
        position: 'relative',
        height,
        overflow: 'hidden',
        mx: -8,
        px: 10,
        pt: 1,
        WebkitMaskImage: 'linear-gradient(#000 72%, transparent)',
        maskImage: 'linear-gradient(#000 72%, transparent)',
      }}
    >
      {children}
    </Box>
  );
}

// The orange "go" button of the page: a link to booking, sign-in or the driver's trips.
const bookButtonSx = (track) => ({
  minHeight: 56,
  px: 4,
  borderRadius: '14px',
  bgcolor: c.BUTTON_PRIMARY,
  color: c.WHITE,
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'none',
  letterSpacing: track('-0.01em'),
  boxShadow: '0 18px 40px -14px rgba(241, 80, 26, 0.75)',
  transition: 'transform 160ms ease-out, background-color 160ms ease-out',
  // The theme makes every button 44 high on touch screens; this one stays 56.
  '@media (pointer: coarse)': { minHeight: 56 },
  '&:hover': { bgcolor: c.BUTTON_PRIMARY_PRESSED, transform: 'translateY(-2px)' },
  '&:active': { transform: 'scale(0.97)' },
  '&:focus-visible': { outline: `3px solid ${ORANGE_LIGHT}`, outlineOffset: 3 },
  '@media (forced-colors: active)': { border: '2px solid ButtonText' },
});

export default function LandingPage(props) {
  const auth = useSelector((state) => state.auth);
  const { t, i18n } = useTranslation();
  const { ...rest } = props;

  // A customer books, a driver goes to their trips, a visitor signs in.
  const profile = auth && auth.profile;
  const loggedIn = Boolean(profile && profile.uid);
  const isDriver = loggedIn && profile.usertype === 'driver';
  const bookTo = loggedIn ? (isDriver ? '/bookings' : '/addbookings') : '/login';

  // Words from /languages first; until those are pushed, the page's own am / om / ti
  // words (switching the language showed English: owner, 5 Nov 2026), then English.
  const L = (key, en) => t(key, { defaultValue: landingWord(i18n.language, key, en) });
  const bookLabel = isDriver ? t('myaccount') : L('lp_cta_book', 'Book a trip');

  // Tight tracking suits Latin headings; Ethiopic letters need their own spacing.
  const ethiopic = /^(am|ti)/.test(String(i18n.language || ''));
  const track = (v) => (ethiopic ? 0 : v);

  const highlights = [
    { id: 'fare', icon: <ReceiptLong />, hue: iconHues.amber, title: L('lp_hl_fare_t', 'Fare first'), desc: L('lp_hl_fare_d', 'Estimate or fixed price, shown before you book.') },
    { id: 'track', icon: <NearMe />, hue: iconHues.info, title: L('lp_hl_track_t', 'Live tracking'), desc: L('lp_hl_track_d', 'Follow your driver on the map until they arrive.') },
    { id: 'pay', icon: <AccountBalanceWallet />, hue: iconHues.success, title: L('lp_hl_pay_t', 'Pay your way'), desc: L('lp_hl_pay_d', 'Cash, transfer, card or online payment, or your Donkey Drive wallet.') },
    // The call centre's short code: a tap calls it from a phone.
    { id: 'help', icon: <SupportAgent />, hue: iconHues.violet, title: L('lp_hl_help_t', 'Help when you need it'), desc: L('lp_hl_help_d', 'Call 9260, day or night.'), href: 'tel:9260' },
  ];

  const steps = [
    { id: 'where', src: iosHome, alt: L('lp_alt_home', 'The Donkey Drive home screen: "Where to?" and your recent places'), title: L('lp_step1_t', 'Where to? Tap and go.'), desc: L('lp_step1_d', 'Your recent places are one tap away.') },
    { id: 'fare', src: iosConfirm, alt: L('lp_alt_confirm', 'The confirmation: the route, the car types and the fare before you book'), title: L('lp_step2_t', 'See your fare first'), desc: L('lp_step2_d', 'Route, car and fare before you confirm.') },
    { id: 'driver', src: iosAccepted, alt: L('lp_alt_accepted', 'Your driver on the way: name, car, plate and minutes away'), title: L('lp_step3_t', 'Know who is coming'), desc: L('lp_step3_d', 'Name, plate and how soon they arrive, at a glance.') },
  ];

  const spotlights = [
    {
      id: 'find', kind: 'android', src: androidFinding, icon: <TravelExplore sx={{ fontSize: 28 }} />, hue: iconHues.info,
      alt: L('lp_alt_finding', 'The app finding a driver, with the cars nearby on the map'),
      title: L('lp_spot_find_t', 'We find your driver'),
      desc: L('lp_spot_find_d', 'See the cars nearby as the search widens, step by step, until a driver accepts.'),
      points: [L('lp_spot_find_b1', 'Nearby cars on the map'), L('lp_spot_find_b2', 'A wider search, one step at a time'), L('lp_spot_find_b3', 'Cancel any time while we search')],
    },
    {
      id: 'stops', kind: 'android', src: androidStops, crop: true, icon: <AltRoute sx={{ fontSize: 28 }} />, hue: iconHues.teal,
      alt: L('lp_alt_stops', 'Adding a third destination to a trip'),
      title: L('lp_spot_stops_t', 'Up to three destinations'),
      desc: L('lp_spot_stops_d', 'Tap + to add a stop to your trip, and − to remove one.'),
      points: [L('lp_spot_stops_b1', 'Add a stop with one tap'), L('lp_spot_stops_b2', 'Up to three destinations in one trip'), L('lp_spot_stops_b3', 'Remove a stop with −')],
    },
    {
      id: 'events', kind: 'ios', src: iosEvents, crop: true, icon: <Celebration sx={{ fontSize: 28 }} />, hue: iconHues.violet,
      alt: L('lp_alt_events', 'Events near you on the home screen'),
      title: L('lp_spot_events_t', 'Going to an event?'),
      desc: L('lp_spot_events_d', 'See events in Addis and book a trip to the venue.'),
      points: [L('lp_spot_events_b1', 'Concerts, football and more, near you'), L('lp_spot_events_b2', 'Your trip to the venue, booked from the event')],
    },
  ];

  return (
    <Box sx={{ bgcolor: c.WHITE, overflow: 'hidden', fontFamily: FONT_STACK }}>
      {/* The browser's 8-px body margin framed the full-width bands in white (the
          other public pages drop it the same way); the fixed header never hides
          the control that has the keyboard focus. */}
      <GlobalStyles styles={{ body: { margin: 0 }, html: { scrollPaddingTop: '160px' } }} />
      <Box
        component="a"
        href="#lp-main"
        sx={{
          position: 'fixed', left: 16, top: 16, zIndex: 1400, px: 2, py: 1.5, borderRadius: '10px',
          bgcolor: c.WHITE, color: c.INK, fontWeight: 700, textDecoration: 'none',
          transform: 'translateY(-200%)',
          '&:focus': { transform: 'none' },
          '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 2 },
        }}
      >
        {L('lp_skip', 'Skip to content')}
      </Box>
      <Header
        color="transparent"
        routes={dashboardRoutes}
        rightLinks={<HeaderLinks />}
        fixed
        changeColorOnScroll={{ height: 50, color: 'white' }}
        {...rest}
      />

      <Box component="main" id="lp-main" tabIndex={-1} sx={{ outline: 'none' }}>
        {/* ── Hero: the night city, the trip drawing itself, the two phones ── */}
        <Box
          component="section"
          aria-labelledby="lp-hero-title"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            color: c.WHITE,
            background: [
              'radial-gradient(55% 60% at 78% 45%, rgba(241, 80, 26, 0.5) 0%, rgba(241, 80, 26, 0) 70%)',
              'radial-gradient(45% 45% at 0% 100%, rgba(199, 61, 14, 0.42) 0%, rgba(199, 61, 14, 0) 70%)',
              `linear-gradient(165deg, ${NIGHT_2} 0%, ${NIGHT} 60%, ${NIGHT_3} 100%)`,
            ].join(', '),
            // The fixed header (and its 100-px logo) stays clear of the first line.
            pt: { xs: 20, md: 17 },
            pb: { xs: 18, md: 20 },
          }}
        >
          <HeroMap />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={{ xs: 7, md: 4 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 0.75, mb: 3, borderRadius: '999px', bgcolor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.16)', ...enter(0) }}>
                  <Box aria-hidden sx={{ position: 'relative', width: 10, height: 10 }}>
                    <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: LIVE_GREEN }} />
                    <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: LIVE_GREEN, opacity: 0, animation: `${ping} 2s ease-out 1s 2`, ...still }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'rgba(255, 255, 255, 0.9)' }}>
                    {L('lp_hero_eyebrow', 'Now in Ethiopia')}
                  </Typography>
                </Box>
                <Typography
                  component="h1"
                  id="lp-hero-title"
                  sx={{ fontWeight: 700, fontSize: { xs: 42, sm: 56, md: 58, lg: 66 }, lineHeight: 1.04, letterSpacing: track('-0.035em'), mb: 3, textWrap: 'balance', ...enter(80) }}
                >
                  {L('lp_hero_title_a', 'Every trip across Addis,')}{' '}
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(90deg, ${ORANGE_LIGHT} 0%, ${ORANGE_GLOW} 45%, ${c.ACCENT} 100%)`,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: ORANGE_ON_NIGHT,
                    }}
                  >
                    {L('lp_hero_title_b', 'one tap away.')}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: { xs: 18, md: 20 }, lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.78)', maxWidth: 540, mb: 4.5, textWrap: 'pretty', ...enter(160) }}>
                  {L('lp_hero_sub', 'See your fare before you book, watch your driver come to you, and pay by cash, transfer, card or wallet.')}
                </Typography>
                <Box sx={enter(240)}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 3 }}>
                    <Button variant="contained" component={RouterLink} to={bookTo} endIcon={<ArrowForward />} sx={bookButtonSx(track)}>
                      {bookLabel}
                    </Button>
                  </Stack>
                  <StoreButtons />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative', height: { xs: 470, sm: 580, md: 560, lg: 620 }, maxWidth: { xs: 360, sm: 560 }, mx: 'auto', ...enter(200) }}>
                  {/* 6 % on a phone: the tilted phone's top corner stays on a 360-wide screen. */}
                  <Box sx={{ position: 'absolute', left: { xs: '6%', sm: '9%', md: '6%', lg: '7%' }, top: { xs: 44, md: 56 }, transform: 'rotate(-7deg)', zIndex: 1 }}>
                    <PhoneFrame
                      kind="android"
                      src={androidHome}
                      alt={L('lp_alt_home_android', 'The home screen on Android: the map, "Find driver" and "Where to?"')}
                      width={{ xs: 'min(190px, 50vw)', sm: 240, md: 215, lg: 250 }}
                      loading="eager"
                      priority
                      onDark
                      sx={{ filter: 'brightness(0.94)' }}
                    />
                  </Box>
                  <Box sx={{ position: 'absolute', right: { xs: '2%', sm: '9%', md: '6%', lg: '7%' }, top: 0, transform: 'rotate(5deg)', zIndex: 2 }}>
                    <PhoneFrame
                      kind="ios"
                      src={iosFinding}
                      alt={L('lp_alt_finding_ios', 'The iPhone app finding a driver, with six cars nearby')}
                      width={{ xs: 'min(214px, 56vw)', sm: 270, md: 250, lg: 285 }}
                      loading="eager"
                      priority
                      onDark
                    />
                  </Box>
                  {/* Over the two phones' meeting edge on purpose, matching the iPhone's screen. */}
                  <FloatCard
                    icon={<LocalTaxi />}
                    iconFg={c.SUCCESS_TEXT}
                    iconBg={c.SUCCESS_TINT}
                    title={L('lp_hero_card_cars', '6 cars nearby')}
                    sub={L('lp_hero_card_cars_sub', 'Finding your driver now')}
                    delay={700}
                    sx={{ left: { xs: '3%', md: '22%' }, bottom: { xs: 12, md: 30, lg: 44 } }}
                  />
                  <FloatCard
                    icon={<ReceiptLong />}
                    iconFg={c.AMBER_TEXT}
                    iconBg={c.AMBER_TINT}
                    title={L('lp_hero_card_fare', 'Fare before you book')}
                    sub="ETB 278.99"
                    delay={900}
                    sx={{ right: { xs: 0, md: 8, lg: -16 }, top: { sm: 270, md: 240, lg: 290 }, display: { xs: 'none', sm: 'block' } }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* ── The four promises, on a card over the hero's edge ── */}
        <Container component="section" aria-labelledby="lp-promises-title" maxWidth="lg" sx={{ position: 'relative', zIndex: 2, mt: { xs: -9, md: -11 } }}>
          <Typography component="h2" id="lp-promises-title" sx={srOnly}>
            {L('lp_promises_title', 'Why customers choose Donkey Drive')}
          </Typography>
          <Reveal>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
                gap: { xs: 3, lg: 0 },
                p: { xs: 3, md: 4 },
                borderRadius: '28px',
                bgcolor: c.WHITE,
                border: `1px solid ${c.OUTLINE}`,
                boxShadow: '0 40px 80px -40px rgba(19, 12, 9, 0.45)',
              }}
            >
              {highlights.map((h, i) => (
                <Box
                  component="li"
                  key={h.id}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: { lg: 2.5 }, borderLeft: { lg: i > 0 ? `1px solid ${c.OUTLINE}` : 'none' } }}
                >
                  <Box aria-hidden sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: h.hue.bg, color: h.hue.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {h.icon}
                  </Box>
                  <Box>
                    <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700, color: c.INK, lineHeight: 1.35, textWrap: 'balance' }}>{h.title}</Typography>
                    {h.href ? (
                      <Typography
                        component="a"
                        href={h.href}
                        sx={{
                          display: 'inline-block',
                          fontSize: 15,
                          fontWeight: 500,
                          color: c.ACCENT_TEXT,
                          lineHeight: 1.5,
                          mt: 0.25,
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                          borderRadius: '4px',
                          '&:hover': { color: c.BUTTON_PRIMARY_PRESSED },
                          '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 2 },
                        }}
                      >
                        {h.desc}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 15, color: c.INK_2, lineHeight: 1.5, mt: 0.25, textWrap: 'pretty' }}>{h.desc}</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Container>

        {/* ── How a booking goes: three steps, three screens ── */}
        <Box component="section" aria-labelledby="lp-how-title" sx={{ py: { xs: 11, md: 15 } }}>
          <Container maxWidth="lg">
            <Reveal>
              <SectionHead
                id="lp-how-title"
                track={track}
                eyebrow={L('lp_how_eyebrow', 'How it works')}
                title={L('lp_how_title', 'Book a trip in three steps')}
                sub={L('lp_how_sub', 'From "Where to?" to your driver at the door, one clear screen at a time.')}
              />
            </Reveal>
            <Box
              component="ol"
              sx={{ listStyle: 'none', p: 0, m: 0, mt: { xs: 7, md: 9 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: { xs: 7, sm: 2.5, md: 4 } }}
            >
              {steps.map((s, i) => (
                <Reveal component="li" key={s.id} delay={i * 120}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: { xs: 5, sm: 3, md: 5 },
                      borderRadius: '28px',
                      background: `linear-gradient(180deg, ${c.ACCENT_TINT} 0%, rgba(255, 244, 239, 0.35) 100%)`,
                      border: `1px solid ${c.OUTLINE}`,
                    }}
                  >
                    <PhoneFrame kind="ios" src={s.src} alt={s.alt} width={{ xs: 220, sm: '78%', md: 200, lg: 230 }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 3, px: 1 }}>
                    <Box aria-hidden sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: c.BUTTON_PRIMARY, color: c.WHITE, fontWeight: 700, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {i + 1}
                    </Box>
                    <Box>
                      <Typography component="h3" sx={{ fontSize: { xs: 21, sm: 18, md: 21 }, fontWeight: 700, color: c.INK, lineHeight: 1.3, letterSpacing: track('-0.01em'), textWrap: 'balance' }}>{s.title}</Typography>
                      <Typography sx={{ fontSize: { xs: 16.5, sm: 15.5, md: 16.5 }, color: c.INK_2, lineHeight: 1.55, mt: 0.5, textWrap: 'pretty' }}>{s.desc}</Typography>
                    </Box>
                  </Box>
                </Reveal>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ── Three features up close ── */}
        <Box component="section" aria-labelledby="lp-spot-title" sx={{ py: { xs: 11, md: 15 }, bgcolor: c.SCREEN_BG }}>
          <Container maxWidth="lg">
            <Reveal>
              <SectionHead
                id="lp-spot-title"
                track={track}
                eyebrow={L('lp_spot_eyebrow', 'Made for Addis')}
                title={L('lp_spot_title', 'The small things, done right')}
                sub={L('lp_spot_sub', 'Designed for the streets of Addis and the phones you use every day.')}
              />
            </Reveal>
            {/* Words first in the page (and on a phone, above the screen); on a wide
                screen the picture alternates sides. The row's margin sits on a wrapper:
                on the Grid it would replace the container's negative spacing margin. */}
            {spotlights.map((s, i) => (
              <Box key={s.id} sx={{ mt: i ? { xs: 10, md: 12 } : { xs: 6, md: 9 } }}>
                <Grid container spacing={{ xs: 5, md: 10 }} alignItems="center">
                  <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: i % 2 ? 1 : 2 } }}>
                    <Reveal delay={100}>
                      <Box aria-hidden sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: s.hue.bg, color: s.hue.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                        {s.icon}
                      </Box>
                      <Typography component="h3" sx={{ fontSize: { xs: 30, md: 38 }, fontWeight: 700, color: c.INK, lineHeight: 1.12, letterSpacing: track('-0.02em'), textWrap: 'balance' }}>
                        {s.title}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 17, md: 19 }, color: c.INK_2, lineHeight: 1.6, mt: 2, mb: 3.5, maxWidth: 520, textWrap: 'pretty' }}>
                        {s.desc}
                      </Typography>
                      <CheckList items={s.points} />
                    </Reveal>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: i % 2 ? 2 : 1 } }}>
                    <Reveal>
                      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', py: { xs: 1, md: 2 } }}>
                        <Box
                          aria-hidden
                          sx={{
                            position: 'absolute',
                            width: { xs: 340, md: 480 },
                            height: { xs: 340, md: 480 },
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            borderRadius: '50%',
                            background: 'radial-gradient(closest-side, rgba(241, 80, 26, 0.18), rgba(241, 80, 26, 0.06) 60%, transparent)',
                          }}
                        />
                        {s.crop ? (
                          <CroppedPhone height={{ xs: 340, md: 440 }}>
                            <PhoneFrame kind={s.kind} src={s.src} alt={s.alt} width={{ xs: 280, md: 350 }} sx={{ transform: `rotate(${i % 2 ? 2 : -2}deg)` }} />
                          </CroppedPhone>
                        ) : (
                          <PhoneFrame kind={s.kind} src={s.src} alt={s.alt} width={{ xs: 236, md: 300 }} sx={{ transform: `rotate(${i % 2 ? 3 : -3}deg)` }} />
                        )}
                      </Box>
                    </Reveal>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Container>
        </Box>

        {/* ── The drivers' side ── */}
        <Box
          component="section"
          aria-labelledby="lp-drivers-title"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            color: c.WHITE,
            py: { xs: 11, md: 15 },
            background: [
              'radial-gradient(45% 60% at 82% 55%, rgba(241, 80, 26, 0.38) 0%, rgba(241, 80, 26, 0) 70%)',
              `linear-gradient(160deg, ${NIGHT_2} 0%, ${NIGHT} 100%)`,
            ].join(', '),
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 7, md: 6 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Reveal>
                  <SectionHead
                    id="lp-drivers-title"
                    center={false}
                    dark
                    track={track}
                    eyebrow={L('lp_drv_eyebrow', 'For drivers')}
                    title={L('lp_drv_title', 'See the fare. Choose your trip.')}
                    sub={L('lp_drv_sub', 'Every request shows the fare, how far the pickup is and how the customer pays.')}
                  />
                  <Box sx={{ mt: 4, mb: 5 }}>
                    <CheckList
                      dark
                      items={[
                        L('lp_drv_b1', 'Highest fare first'),
                        L('lp_drv_b2', 'Fixed price or estimate, always marked'),
                        L('lp_drv_b3', 'A 3D view of the road to every pickup'),
                        L('lp_drv_b4', 'Slide to finish the trip'),
                        L('lp_drv_b5', 'Lower commission, so you keep more'),
                      ]}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'rgba(255, 255, 255, 0.7)', mb: 1.5 }}>
                    {L('lp_drv_cta', 'Get the app and start driving')}
                  </Typography>
                  <StoreButtons />
                </Reveal>
              </Grid>
              <Grid item xs={12} md={6}>
                <Reveal delay={120}>
                  <Box sx={{ position: 'relative', height: { xs: 480, sm: 560, md: 600 }, maxWidth: { xs: 360, sm: 540 }, mx: 'auto' }}>
                    <Box sx={{ position: 'absolute', left: '2%', top: { xs: 40, md: 60 }, transform: 'rotate(-6deg)', zIndex: 1 }}>
                      <PhoneFrame kind="android" src={androidDrive} alt={L('lp_alt_drive', 'The driver\'s 3D view of the trip, with the "Slide to complete trip" slider')} width={{ xs: 'min(190px, 50vw)', sm: 230, md: 245 }} onDark sx={{ filter: 'brightness(0.94)' }} />
                    </Box>
                    <Box sx={{ position: 'absolute', right: '2%', top: 0, transform: 'rotate(4deg)', zIndex: 2 }}>
                      <PhoneFrame kind="ios" src={iosRequests} alt={L('lp_alt_requests', "The driver's requests, highest fare first")} width={{ xs: 'min(205px, 54vw)', sm: 255, md: 275 }} onDark />
                    </Box>
                  </Box>
                </Reveal>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* ── The tutorial videos, fetched only when the visitor comes near them ── */}
        <Box component="section" aria-labelledby="lp-videos-title" sx={{ py: { xs: 11, md: 15 }, bgcolor: c.WHITE }}>
          <Container maxWidth="lg">
            <Reveal>
              <SectionHead
                id="lp-videos-title"
                track={track}
                eyebrow={L('lp_videos_eyebrow', 'See it in action')}
                title={L('lp_videos_title', 'Learn the app in our videos')}
              />
            </Reveal>
            <NearViewport
              minHeight={{ xs: 300, sm: 560, md: 640 }}
              sx={{
                mt: { xs: 5, md: 7 },
                maxWidth: 880,
                mx: 'auto',
                // The shared player, styled from here: rounder, and its thumbnail row
                // no wider than the player so it can be swiped on a phone. Until the
                // intro's frame arrives, a picture of it (the player's poster is the
                // video file itself, which is no picture): only on the intro, the one
                // whose poster names it.
                '& .video-container': { borderRadius: '24px', overflow: 'hidden' },
                '& .video-container video[poster*="Intro.mp4"]': {
                  backgroundImage: `url(${videoPoster})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                },
                // (border-box: its 8-px padding stays inside the player's width)
                '& .video-container + div': { maxWidth: '100%', boxSizing: 'border-box' },
              }}
            >
              <VideoCarousel videoSources={videoSources} />
            </NearViewport>
          </Container>
        </Box>

        {/* ── Download: the stores, and three phones rising out of the band ── */}
        <Box component="section" aria-labelledby="lp-download-title" sx={{ pb: { xs: 11, md: 15 }, bgcolor: c.WHITE }}>
          <Container maxWidth="lg">
            <Reveal>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: { xs: '28px', md: '40px' },
                  color: c.WHITE,
                  px: { xs: 2.5, sm: 5, md: 8 },
                  pt: { xs: 6, md: 9 },
                  // The words sit on the button orange (white 5.1:1); the far corner
                  // ends on the brand orange of the footer below.
                  background: [
                    'radial-gradient(55% 75% at 88% 100%, rgba(255, 140, 90, 0.4) 0%, rgba(255, 140, 90, 0) 70%)',
                    `linear-gradient(135deg, ${c.BUTTON_PRIMARY} 0%, ${c.BUTTON_PRIMARY} 45%, ${c.ACCENT} 100%)`,
                  ].join(', '),
                  boxShadow: '0 30px 60px -30px rgba(169, 51, 11, 0.45)',
                }}
              >
                <Grid container spacing={{ xs: 5, md: 4 }} alignItems="flex-end">
                  <Grid item xs={12} md={6} sx={{ pb: { md: 9 } }}>
                    <Typography component="h2" id="lp-download-title" sx={{ fontWeight: 700, fontSize: { xs: 34, md: 46 }, lineHeight: 1.1, letterSpacing: track('-0.025em'), textWrap: 'balance' }}>
                      {L('lp_dl_title', 'Donkey Drive on your phone')}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, mt: 2, mb: 4, color: c.WHITE, maxWidth: 480, textWrap: 'pretty' }}>
                      {L('lp_dl_sub', 'One app for customers and drivers, on iPhone and Android.')}
                    </Typography>
                    <StoreButtons ring={c.WHITE} />
                    <Button
                      component={RouterLink}
                      to={bookTo}
                      endIcon={<ArrowForward />}
                      sx={{
                        mt: 2.5,
                        width: { xs: '100%', sm: 'auto' },
                        minHeight: 48,
                        px: 2.5,
                        borderRadius: '12px',
                        color: c.WHITE,
                        fontSize: 16,
                        fontWeight: 700,
                        textTransform: 'none',
                        border: '1.5px solid rgba(255, 255, 255, 0.7)',
                        '@media (pointer: coarse)': { minHeight: 48 },
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)', borderColor: c.WHITE },
                        '&:focus-visible': { outline: `3px solid ${c.WHITE}`, outlineOffset: 3 },
                      }}
                    >
                      {isDriver ? t('myaccount') : L('lp_dl_web', 'Or book on the web')}
                    </Button>
                  </Grid>
                  {/* Screens already described above: decoration here. */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative', height: { xs: 300, sm: 360, md: 440 }, maxWidth: 520, mx: 'auto' }}>
                      <Box sx={{ position: 'absolute', left: { xs: '0%', sm: '6%' }, bottom: { xs: -150, md: -170 }, transform: 'rotate(-10deg)', zIndex: 1 }}>
                        <PhoneFrame kind="android" src={androidAccepted} alt="" width={{ xs: 150, sm: 180, md: 205 }} sx={{ filter: 'brightness(0.95)' }} />
                      </Box>
                      <Box sx={{ position: 'absolute', left: '50%', bottom: { xs: -110, md: -120 }, transform: 'translateX(-50%)', zIndex: 2 }}>
                        <PhoneFrame kind="ios" src={iosConfirm} alt="" width={{ xs: 170, sm: 205, md: 235 }} />
                      </Box>
                      <Box sx={{ position: 'absolute', right: { xs: '0%', sm: '6%' }, bottom: { xs: -150, md: -170 }, transform: 'rotate(10deg)', zIndex: 1 }}>
                        <PhoneFrame kind="android" src={androidFinding} alt="" width={{ xs: 150, sm: 180, md: 205 }} sx={{ filter: 'brightness(0.95)' }} />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Reveal>
          </Container>
        </Box>
      </Box>

      <HomeFooter />
    </Box>
  );
}
